import React, { useState } from 'react';
import {
  RotateCcw,
  Plus,
  Search,
  Loader2,
  ChevronDown,
  ChevronRight,
  Clock,
  Send,
  XCircle,
  MoreHorizontal,
  Eye,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';

interface StockReturn {
  id: string;
  organization_id: string;
  location_id: string;
  supplier_id: string | null;
  return_number: string;
  status: 'open' | 'sent' | 'cancelled';
  supplier_invoice_number: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  supplier?: { id: string; name: string } | null;
  location?: { id: string; name: string } | null;
  items?: Array<{ quantity: number; unit_cost: number }> | null;
}

interface StockReturnItem {
  id: string;
  return_id: string;
  product_id: string | null;
  quantity: number;
  unit_cost: number;
  reason: string | null;
  notes: string | null;
  product?: { id: string; name: string; sku: string | null } | null;
}

interface ProductAvailability {
  product_id: string;
  product_name: string;
  sku: string | null;
  quantity_on_hand: number;
  quantity_reserved: number;
  quantity_available: number;
  quantity_boh: number;
  quantity_foh: number;
  cost: number;
}

const formatQuantity = (value: number) => value.toLocaleString(undefined, {
  maximumFractionDigits: 3,
});

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: Clock },
  sent: { label: 'Sent', color: 'bg-green-100 text-green-700', icon: Send },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function StockReturns() {
  const { organization, user } = useAuth();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [outletFilter, setOutletFilter] = useState<string>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<StockReturn | null>(null);
  const [selectedReturnItems, setSelectedReturnItems] = useState<StockReturnItem[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  // Form state for creating returns
  const [formData, setFormData] = useState({
    location_id: '',
    supplier_id: '',
    supplier_invoice_number: '',
    notes: '',
  });
  const [formItems, setFormItems] = useState<{ product_id: string; quantity: string; unit_cost: string; reason: string }[]>([
    { product_id: '', quantity: '', unit_cost: '', reason: '' },
  ]);

  // Fetch suppliers
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
    enabled: !!organization?.id,
  });

  // Fetch stock returns
  const { data: stockReturns = [], isLoading } = useQuery({
    queryKey: ['stock-returns', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('stock_returns')
        .select(`
          *,
          supplier:suppliers(id, name),
          location:locations(id, name),
          items:stock_return_items(quantity, unit_cost)
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as StockReturn[];
    },
    enabled: !!organization?.id,
  });

  const {
    data: locationAvailability = [],
    isLoading: availabilityLoading,
    error: availabilityError,
    refetch: refetchAvailability,
  } = useQuery({
    queryKey: ['product-location-availability', formData.location_id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_product_location_availability', {
        p_location_id: formData.location_id,
      });
      if (error) throw error;
      return (Array.isArray(data) ? data : []) as ProductAvailability[];
    },
    enabled: createDialogOpen && !!formData.location_id,
    staleTime: 15_000,
  });

  const availabilityByProductId = new Map(
    locationAvailability.map((row) => [row.product_id, row] as const),
  );

  const hasUnavailableReturnQuantity = formItems.some((item) => {
    if (!item.product_id || !item.quantity) return false;
    const quantity = Number(item.quantity);
    const available = availabilityByProductId.get(item.product_id)?.quantity_available ?? 0;
    return !Number.isFinite(quantity) || quantity <= 0 || quantity > available;
  });

  // Fetch return items
  const fetchReturnItems = async (returnId: string) => {
    const { data, error } = await supabase
      .from('stock_return_items')
      .select(`
        *,
        product:products(id, name, sku)
      `)
      .eq('return_id', returnId);
    if (error) throw error;
    return data as StockReturnItem[];
  };

  // Generate return number
  const generateReturnNumber = async (): Promise<string> => {
    const { data: existing } = await supabase
      .from('stock_returns')
      .select('return_number')
      .eq('organization_id', organization?.id || '')
      .order('created_at', { ascending: false })
      .limit(1);

    let nextNum = 1;
    if (existing && existing.length > 0) {
      const lastNum = existing[0].return_number?.replace(/[^0-9]/g, '');
      if (lastNum) nextNum = (parseInt(lastNum, 10) || 0) + 1;
    }
    return `RET-${nextNum.toString().padStart(4, '0')}`;
  };

  // Create return mutation
  const createReturn = useMutation({
    mutationFn: async () => {
      if (!organization?.id) throw new Error('No organization');
      if (!formData.location_id) throw new Error('Please select an outlet');
      if (!formData.supplier_id) throw new Error('Please select a supplier');

      const validItems = formItems.filter(item => item.product_id && parseFloat(item.quantity) > 0);
      if (validItems.length === 0) throw new Error('Add at least one item');

      const latestAvailability = await refetchAvailability();
      if (latestAvailability.error) throw latestAvailability.error;
      const latestByProductId = new Map(
        (latestAvailability.data || []).map((row) => [row.product_id, row] as const),
      );
      const unavailableItem = validItems.find((item) => {
        const available = latestByProductId.get(item.product_id)?.quantity_available ?? 0;
        return parseFloat(item.quantity) > available;
      });
      if (unavailableItem) {
        const availability = latestByProductId.get(unavailableItem.product_id);
        throw new Error(
          `${availability?.product_name || 'This product'} has ${formatQuantity(availability?.quantity_available ?? 0)} available at the selected outlet, but ${formatQuantity(parseFloat(unavailableItem.quantity))} was entered.`,
        );
      }

      const returnNumber = await generateReturnNumber();

      const { data: stockReturn, error: returnError } = await supabase
        .from('stock_returns')
        .insert({
          organization_id: organization.id,
          location_id: formData.location_id,
          supplier_id: formData.supplier_id,
          return_number: returnNumber,
          status: 'open',
          supplier_invoice_number: formData.supplier_invoice_number || null,
          notes: formData.notes || null,
          created_by: user?.id || null,
        })
        .select()
        .single();

      if (returnError) throw returnError;

      const itemsToInsert = validItems.map(item => ({
        return_id: stockReturn.id,
        product_id: item.product_id,
        quantity: parseFloat(item.quantity),
        unit_cost: parseFloat(item.unit_cost) || 0,
        reason: item.reason || null,
      }));

      const { error: itemsError } = await supabase
        .from('stock_return_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      return stockReturn;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-returns'] });
      toast.success('Stock return created');
      setCreateDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      setFormError(error.message);
      toast.error('Failed to create return', { description: error.message });
    },
  });

  // Update return status
  const updateReturnStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      if (status === 'sent') {
        const { error } = await supabase.rpc('send_stock_return', {
          p_return_id: id,
        });
        if (error) throw error;
        return;
      }

      const { error } = await supabase
        .from('stock_returns')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stock-returns'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-by-product'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      queryClient.invalidateQueries({ queryKey: ['product-location-availability'] });
      if (variables.status === 'sent') {
        setViewDialogOpen(false);
        toast.success('Return sent', { description: 'The returned quantity was deducted from outlet inventory.' });
      } else {
        toast.success('Return status updated');
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to update status', { description: error.message });
    },
  });

  // Delete return
  const deleteReturn = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('stock_returns')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-returns'] });
      toast.success('Return deleted');
    },
    onError: (error: Error) => {
      toast.error('Failed to delete return', { description: error.message });
    },
  });

  const openViewDialog = async (ret: StockReturn) => {
    setSelectedReturn(ret);
    const items = await fetchReturnItems(ret.id);
    setSelectedReturnItems(items);
    setViewDialogOpen(true);
  };

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const resetForm = () => {
    setFormData({ location_id: '', supplier_id: '', supplier_invoice_number: '', notes: '' });
    setFormItems([{ product_id: '', quantity: '', unit_cost: '', reason: '' }]);
    setFormError(null);
  };

  // Filter returns
  const filteredReturns = stockReturns.filter(ret => {
    if (statusFilter !== 'all' && ret.status !== statusFilter) return false;
    if (outletFilter !== 'all' && ret.location_id !== outletFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchReturn = ret.return_number?.toLowerCase().includes(q);
      const matchSupplier = ret.supplier?.name?.toLowerCase().includes(q);
      const matchInvoice = ret.supplier_invoice_number?.toLowerCase().includes(q);
      if (!matchReturn && !matchSupplier && !matchInvoice) return false;
    }
    return true;
  });

  const handleCreateReturn = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (availabilityLoading) {
      setFormError('Wait for outlet inventory availability to finish loading');
      return;
    }
    if (availabilityError) {
      setFormError('Outlet inventory availability could not be loaded. Refresh and try again.');
      return;
    }
    if (hasUnavailableReturnQuantity) {
      setFormError('One or more return quantities exceed the stock available at this outlet');
      return;
    }
    createReturn.mutate();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-base font-medium">Stock Returns</h3>
        </div>
        <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Create Return
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="min-w-[150px]">
          <Label className="mb-1.5 block">Show</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All returns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All returns</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-[200px] max-w-sm">
          <Label className="mb-1.5 block">Search returns</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Enter return number, supplier invoice number, reference or product"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-w-[160px]">
          <Label className="mb-1.5 block">Outlet</Label>
          <Select value={outletFilter} onValueChange={setOutletFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All outlets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outlets</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Returns Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading returns...</span>
          </div>
        </div>
      ) : filteredReturns.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <RotateCcw className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No stock returns</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create a stock return for damaged, faulty, or overstocked items
              </p>
              <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Create Return
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="w-10 px-2"></th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Reference
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  From
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  To
                </th>
                <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">
                  Status
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Created
                </th>
                <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                  Total qty.
                </th>
                <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                  Total cost
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredReturns.map((ret) => {
                const statusConfig = STATUS_CONFIG[ret.status];
                const StatusIcon = statusConfig.icon;
                const totalQty = (ret.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0);
                const totalCost = (ret.items || []).reduce((sum, item) => sum + ((item.quantity || 0) * (item.unit_cost || 0)), 0);
                const isExpanded = expandedRows.has(ret.id);

                return (
                  <React.Fragment key={ret.id}>
                    <tr className="hover:bg-muted/30 transition-colors">
                      <td className="px-2 py-3">
                        <button
                          onClick={() => toggleRow(ret.id)}
                          className="p-1 hover:bg-muted rounded"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openViewDialog(ret)}
                          className="font-medium text-sm text-primary hover:underline"
                        >
                          Return - {new Date(ret.created_at).toLocaleDateString('en-US', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {ret.location?.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {ret.supplier?.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={cn('gap-1', statusConfig.color)}>
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {new Date(ret.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">
                        {totalQty < 0 ? totalQty : `-${totalQty}`}
                      </td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">
                        {formatCurrency(totalCost)}
                      </td>
                      <td className="px-2 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openViewDialog(ret)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {ret.status === 'open' && (
                              <DropdownMenuItem
                                onClick={() => updateReturnStatus.mutate({ id: ret.id, status: 'sent' })}
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Mark as Sent
                              </DropdownMenuItem>
                            )}
                            {ret.status === 'open' && (
                              <DropdownMenuItem
                                onClick={() => {
                                  if (confirm('Cancel this return?')) {
                                    updateReturnStatus.mutate({ id: ret.id, status: 'cancelled' });
                                  }
                                }}
                                className="text-red-600"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Cancel Return
                              </DropdownMenuItem>
                            )}
                            {ret.status === 'open' && (
                              <DropdownMenuItem
                                onClick={() => {
                                  if (confirm('Delete this return?')) {
                                    deleteReturn.mutate(ret.id);
                                  }
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                    {/* Expanded row details */}
                    {isExpanded && (
                      <tr className="bg-muted/20">
                        <td colSpan={9} className="px-12 py-4">
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="font-medium">Supplier invoice number</span>
                              <p className="text-muted-foreground">
                                {ret.supplier_invoice_number || '-'}
                              </p>
                            </div>
                            <div>
                              <span className="font-medium">Return number</span>
                              <p className="text-muted-foreground">{ret.return_number}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Return Dialog */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent maxWidth="3xl">
          <form onSubmit={handleCreateReturn}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Create Stock Return</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Return damaged, faulty, or overstocked items to a supplier
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody className="max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                {/* Info section */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Outlet *</Label>
                    <Select
                      value={formData.location_id}
                      onValueChange={(v) => {
                        setFormData({ ...formData, location_id: v });
                        setFormError(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select outlet" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Supplier *</Label>
                    <Select
                      value={formData.supplier_id}
                      onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Supplier invoice number</Label>
                    <Input
                      value={formData.supplier_invoice_number}
                      onChange={(e) => setFormData({ ...formData, supplier_invoice_number: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Optional notes"
                    />
                  </div>
                </div>

                {formError && (
                  <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{formError}</span>
                  </div>
                )}

                {formData.location_id && availabilityLoading && (
                  <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading current stock for this outlet...
                  </div>
                )}

                {formData.location_id && availabilityError && (
                  <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                    Stock availability could not be loaded. Close this window, refresh, and try again.
                  </div>
                )}

                {/* Line items */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Return Items</Label>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                            Product
                          </th>
                          <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-24">
                            Qty
                          </th>
                          <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                            Unit Cost
                          </th>
                          <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-40">
                            Reason
                          </th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {formItems.map((item, idx) => {
                          const availability = availabilityByProductId.get(item.product_id);
                          const quantity = Number(item.quantity);
                          const exceedsAvailability = !!item.product_id
                            && !!item.quantity
                            && Number.isFinite(quantity)
                            && quantity > (availability?.quantity_available ?? 0);
                          return (
                          <tr key={idx} className={cn(exceedsAvailability && 'bg-red-500/5')}>
                            <td className="px-4 py-2">
                              <Select
                                value={item.product_id}
                                disabled={!formData.location_id || availabilityLoading || !!availabilityError}
                                onValueChange={(v) => {
                                  const updated = [...formItems];
                                  updated[idx].product_id = v;
                                  const productAvailability = availabilityByProductId.get(v);
                                  if (productAvailability?.cost) updated[idx].unit_cost = String(productAvailability.cost);
                                  setFormItems(updated);
                                }}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select product" />
                                </SelectTrigger>
                                <SelectContent>
                                  {locationAvailability.map((productAvailability) => {
                                    return (
                                      <SelectItem
                                        key={productAvailability.product_id}
                                        value={productAvailability.product_id}
                                        disabled={
                                          !!formData.location_id
                                          && productAvailability.quantity_available <= 0
                                          && item.product_id !== productAvailability.product_id
                                        }
                                      >
                                        {productAvailability.product_name} · {formatQuantity(productAvailability.quantity_available)} available
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              {item.product_id && (
                                <p className={cn(
                                  'mt-1.5 text-xs',
                                  exceedsAvailability ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground',
                                )}>
                                  <span className="font-medium text-foreground">
                                    {formatQuantity(availability?.quantity_available ?? 0)} available
                                  </span>
                                  {' '}at this outlet
                                  {(availability?.quantity_reserved ?? 0) > 0 && (
                                    <> · {formatQuantity(availability?.quantity_reserved ?? 0)} reserved</>
                                  )}
                                  {' '}· {formatQuantity(availability?.quantity_boh ?? 0)} BOH / {formatQuantity(availability?.quantity_foh ?? 0)} FOH
                                </p>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min="0.001"
                                max={availability?.quantity_available}
                                step="any"
                                value={item.quantity}
                                onChange={(e) => {
                                  const updated = [...formItems];
                                  updated[idx].quantity = e.target.value;
                                  setFormItems(updated);
                                }}
                                placeholder="0"
                                className="text-right"
                                aria-invalid={exceedsAvailability}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_cost}
                                onChange={(e) => {
                                  const updated = [...formItems];
                                  updated[idx].unit_cost = e.target.value;
                                  setFormItems(updated);
                                }}
                                placeholder="0.00"
                                className="text-right"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Select
                                value={item.reason}
                                onValueChange={(v) => {
                                  const updated = [...formItems];
                                  updated[idx].reason = v;
                                  setFormItems(updated);
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Reason" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="damaged">Damaged</SelectItem>
                                  <SelectItem value="faulty">Faulty</SelectItem>
                                  <SelectItem value="expired">Expired</SelectItem>
                                  <SelectItem value="overstock">Overstock</SelectItem>
                                  <SelectItem value="wrong_item">Wrong Item</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-2">
                              {formItems.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => {
                                    const updated = formItems.filter((_, i) => i !== idx);
                                    setFormItems(updated);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    className="mt-2 gap-2"
                    onClick={() => setFormItems([...formItems, { product_id: '', quantity: '', unit_cost: '', reason: '' }])}
                    disabled={!formData.location_id}
                  >
                    <Plus className="h-4 w-4" />
                    Add line
                  </Button>
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="blaze"
                disabled={
                  createReturn.isPending
                  || availabilityLoading
                  || !!availabilityError
                  || hasUnavailableReturnQuantity
                }
              >
                {createReturn.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create Return
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* View Return Detail Modal */}
      <ResponsiveModal open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <ResponsiveModalContent maxWidth="4xl">
          {/* Header */}
          <div className="shrink-0 border-b px-6 pr-12 py-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold">
                  {selectedReturn?.return_number}{' '}
                  <span className="text-muted-foreground font-normal">
                    ({selectedReturn ? STATUS_CONFIG[selectedReturn.status].label : ''})
                  </span>
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {selectedReturn?.status === 'open' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    disabled={updateReturnStatus.isPending}
                    onClick={() => {
                      if (selectedReturn) {
                        updateReturnStatus.mutate({ id: selectedReturn.id, status: 'sent' });
                      }
                    }}
                  >
                    <Send className="h-4 w-4" />
                    Mark as Sent
                  </Button>
                )}
              </div>
            </div>
          </div>

          <ResponsiveModalBody className="max-h-[70vh] overflow-y-auto">
            <div className="space-y-6">
              {/* Two-column info grid */}
              <div className="grid grid-cols-2 gap-x-12 gap-y-3 py-4">
                <div className="space-y-3">
                  <div className="flex">
                    <span className="w-40 font-semibold text-sm">From</span>
                    <span className="text-sm">{selectedReturn?.location?.name || '-'}</span>
                  </div>
                  <div className="flex">
                    <span className="w-40 font-semibold text-sm">Supplier</span>
                    <span className="text-sm">{selectedReturn?.supplier?.name || '-'}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex">
                    <span className="w-40 font-semibold text-sm">Created</span>
                    <span className="text-sm">
                      {selectedReturn ? new Date(selectedReturn.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      }) : '-'}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-40 font-semibold text-sm">Supplier invoice</span>
                    <span className="text-sm">{selectedReturn?.supplier_invoice_number || '-'}</span>
                  </div>
                  {selectedReturn?.notes && (
                    <div className="flex">
                      <span className="w-40 font-semibold text-sm">Note</span>
                      <span className="text-sm">{selectedReturn.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Totals before table */}
              <div className="space-y-1">
                <div className="flex gap-2">
                  <span className="font-semibold text-sm">Total cost</span>
                  <span className="text-sm">
                    {formatCurrency(
                      selectedReturnItems.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0)
                    )}
                  </span>
                </div>
              </div>

              {/* Items table */}
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-12">
                        #
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                        Product
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                        SKU
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                        Reason
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-24">
                        Quantity
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                        Cost price
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 w-28">
                        Total cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedReturnItems.map((item, idx) => (
                      <tr key={item.id} className="hover:bg-muted/20">
                        <td className="px-4 py-3 text-sm text-muted-foreground">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-sm">{item.product?.name || 'Unknown'}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {item.product?.sku || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground capitalize">
                          {item.reason?.replace('_', ' ') || '-'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-sm">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-sm">
                          {formatCurrency(item.unit_cost)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium tabular-nums text-sm">
                          {formatCurrency(item.quantity * item.unit_cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-amber-50 dark:bg-amber-950/30 font-medium">
                      <td colSpan={4} className="px-4 py-3 text-sm font-semibold">Total</td>
                      <td className="px-4 py-3 text-right tabular-nums text-sm font-semibold">
                        {selectedReturnItems.reduce((sum, item) => sum + item.quantity, 0)}
                      </td>
                      <td></td>
                      <td className="px-4 py-3 text-right tabular-nums text-sm font-semibold">
                        {formatCurrency(selectedReturnItems.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
